import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { legalNameWarnings } from "@/lib/legalNameRule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Clock, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import {
  AddressFields,
  AddressValue,
  EMPTY_ADDRESS,
  addressIsComplete,
  addressLooksLikeMailbox,
  toAddress,
} from "@/components/onboarding/AddressFields";
import { StepShell } from "@/components/onboarding/StepShell";
import { properName, unaTitleCase } from "@/lib/names";
import {
  addressLines,
  hasSeparateShipping,
  shippingAddressOf,
} from "@/lib/shippingDestination";
import { isSuitePath } from "@/lib/onboardingComplete";
import { OnboardingShell, resolveOnboardingPhase } from "@/components/onboarding/OnboardingShell";
import {
  AgreementPanel,
  AgreementSignedCard,
} from "@/components/onboarding/AgreementStep";
import { LeasePanel, LeaseSignedCard } from "@/components/onboarding/LeaseStep";
import { CompletionCelebration } from "@/components/onboarding/CompletionCelebration";

import {
  PLATFORM_ADDRESS_MONTHLY_FEE,
  PLATFORM_ADDRESS_TEMPLATE,
} from "@/config/integration-placeholders";

type MemberRecord = {
  id: string;
  user_id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  ontraport_contact_id: string | null;
  legal_full_name: string | null;
  phone: string | null;
  principal_officer_is_self: boolean | null;
  principal_officer_name: string | null;
  principal_officer_email: string | null;
  legal_address: unknown;
  mailing_address: unknown;
  shipping_line1: string | null;
  shipping_line2: string | null;
  shipping_city: string | null;
  shipping_state: string | null;
  shipping_zip: string | null;
  home_situation: string | null;
  spouse_name: string | null;
  adult_children_count: number | null;
  finance_segment: string | null;
  chancery_interest: boolean | null;
  chancery_interest_at: string | null;

  una_name: string | null;
  una_name_status: string | null;

  una_name_confirmed_at: string | null;
  una_name_review_note: string | null;
  ca_address_option: string | null;
  ca_address: unknown;
  suite_number: number | null;
  lease_status: string | null;
  payment_status: string | null;
  mea_status: string | null;
  mea_signed_at: string | null;
  una_amount: string | null;
  monthly_amount: string | null;
};

type StepKey =
  | "principal_officer"
  | "legal_name"
  | "phone"
  | "legal_address"
  | "shipping_address"
  | "home_situation"
  | "spouse"
  | "adult_children"
  | "finances"
  | "una_name"
  | "address"
  | "lease"
  | "payment"
  | "complete";

const HOME_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "solo", label: "It's just me." },
  { value: "us", label: "It's just us." },
  { value: "me_kids", label: "It's me + kid(s)." },
  { value: "us_kids", label: "It's us + kid(s)." },
];

// Includes legacy values that can no longer be selected but may still be stored.
const HOME_LABELS: Record<string, string> = {
  ...Object.fromEntries(HOME_OPTIONS.map((o) => [o.value, o.label])),
  us_kids_grown: "It's just us + kid(s) are grown.",
  me_kids_grown: "It's just me + kid(s) are grown.",
};


const FINANCE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "seg_solo", label: "I handle it all myself. I like to verify everything personally." },
  { value: "seg_seasonal", label: "We keep it simple. We see a tax professional about once a year." },
  {
    value: "seg_counseled",
    label: "I have an entity or two and standing relationships with a CPA or attorney.",
  },
  {
    value: "seg_house",
    label: "We have established planning in place: trusts, advisors, possibly a foundation.",
  },
];

const BANNED_NAME_WORDS = [
  "family",
  "trust",
  "enterprises",
  "enterprise",
  "incorporated",
  "corporation",
  "company",
  "foundation",
  "charity",
  "co-op",
  "coop",
  "cooperative",
  "inc",
  "corp",
  "llc",
];

function nameWarnings(name: string): string[] {
  const out: string[] = [];
  const trimmed = name.trim();
  if (!trimmed) return out;

  for (const word of BANNED_NAME_WORDS) {
    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`(?:^|[^a-z0-9])${escaped}\\.?(?![a-z0-9])`, "i");
    if (re.test(trimmed)) {
      out.push(`This name includes a word we cannot use: ${word}. Choose a wording without it.`);
    }
  }

  if (/^the\s/i.test(trimmed)) {
    out.push("Names should not lead with The. Try it without.");
  }

  const words = trimmed.split(/\s+/);
  if (words.length > 4) {
    out.push("Keep it to four words at most. Two or three is the sweet spot.");
  }
  // The same rule the government forms use for a legal name.
  out.push(...legalNameWarnings(trimmed));

  return out;
}


function OptionButton({
  selected,
  label,
  onClick,
}: {
  selected: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-lg border p-4 text-left text-sm leading-relaxed transition-colors min-h-14 ${
        selected
          ? "border-primary bg-primary/5 ring-1 ring-primary"
          : "border-border hover:border-primary/50 hover:bg-muted/40"
      }`}
    >
      {label}
    </button>
  );
}

export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [record, setRecord] = useState<MemberRecord | null>(null);
  const [stepKey, setStepKey] = useState<StepKey>("principal_officer");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const startedRef = useRef(false);

  // Draft values
  const [officerIsSelf, setOfficerIsSelf] = useState<boolean | null>(null);
  const [officerName, setOfficerName] = useState("");
  const [officerEmail, setOfficerEmail] = useState("");
  const [legalName, setLegalName] = useState("");
  const [phone, setPhone] = useState("");
  const [legalAddress, setLegalAddress] = useState<AddressValue>(EMPTY_ADDRESS);
  const [shippingChoice, setShippingChoice] = useState<"legal" | "different" | null>(null);
  const [shippingAddress, setShippingAddress] = useState<AddressValue>(EMPTY_ADDRESS);
  const [homeSituation, setHomeSituation] = useState<string | null>(null);
  const [spouseName, setSpouseName] = useState("");
  const [hasAdultKids, setHasAdultKids] = useState<boolean | null>(null);
  const [adultKidsCount, setAdultKidsCount] = useState("");
  const [financeSegment, setFinanceSegment] = useState<string | null>(null);
  const [chanceryInterest, setChanceryInterest] = useState<boolean | null>(null);

  const [unaName, setUnaName] = useState("");
  

  const [caAddress, setCaAddress] = useState<AddressValue>(EMPTY_ADDRESS);
  const [addressChoice, setAddressChoice] = useState<string | null>(null);
  const [platformConfirmed, setPlatformConfirmed] = useState(false);
  const [nameChecked, setNameChecked] = useState(false);
  const [fixingName, setFixingName] = useState(false);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [nameCelebration, setNameCelebration] = useState<string | null>(null);
  const [isResubmission, setIsResubmission] = useState(false);
  const [startingCheckout, setStartingCheckout] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(
    () => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("paid") === "1",
  );
  const leaseSentRef = useRef(false);


  const hydrate = useCallback((row: MemberRecord) => {
    setRecord(row);
    setOfficerIsSelf(row.principal_officer_is_self);
    setOfficerName(row.principal_officer_name ?? "");
    setOfficerEmail(row.principal_officer_email ?? "");
    setLegalName(row.legal_full_name ?? "");
    setPhone(row.phone ?? "");
    setLegalAddress(toAddress(row.legal_address));
    setShippingAddress(shippingAddressOf(row));
    setShippingChoice(hasSeparateShipping(row) ? "different" : "legal");
    setHomeSituation(row.home_situation);
    setSpouseName(row.spouse_name ?? "");
    if (typeof row.adult_children_count === "number") {
      setHasAdultKids(row.adult_children_count > 0);
      setAdultKidsCount(String(row.adult_children_count));
    }
    setFinanceSegment(row.finance_segment);
    setChanceryInterest(row.chancery_interest ?? null);

    setUnaName(row.una_name ?? "");
    setCaAddress(toAddress(row.ca_address));
    setAddressChoice(row.ca_address_option);
  }, []);

  const pushStage = useCallback(async (stage: string) => {
    try {
      await supabase.functions.invoke("onboarding-stage", { body: { stage } });
    } catch (_e) {
      // Stage syncing never blocks a member.
    }
  }, []);

  const saveState = useCallback(
    async (memberRecordId: string, next: StepKey, extra: Record<string, unknown> = {}) => {
      await (supabase as any).from("onboarding_state").upsert(
        {
          member_record_id: memberRecordId,
          current_step: next,
          ...extra,
        },
        { onConflict: "member_record_id" },
      );
    },
    [],
  );

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!user) return;
      const { data: row, error } = await (supabase as any)
        .from("member_records")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!active) return;
      if (error || !row) {
        setLoading(false);
        return;
      }
      hydrate(row as MemberRecord);

      const { data: state } = await (supabase as any)
        .from("onboarding_state")
        .select("*")
        .eq("member_record_id", (row as MemberRecord).id)
        .maybeSingle();

      if (!active) return;

      // Until Begin has been clicked once, every entry lands on the intro screen.
      if (!state?.intro_acknowledged_at) {
        navigate("/welcome/success", { replace: true });
        return;
      }

      if (state?.current_step) {
        // The mailing address step retired. Anyone parked there resumes at shipping.
        const saved = state.current_step === "mailing_address" ? "shipping_address" : state.current_step;
        setStepKey(saved as StepKey);
      } else {
        await saveState((row as MemberRecord).id, "principal_officer");
        if (!startedRef.current) {
          startedRef.current = true;
          void pushStage("intake_started");
        }
      }
      setLoading(false);
    };
    void load();
    return () => {
      active = false;
    };
  }, [user, hydrate, saveState, pushStage, navigate]);

  const refresh = useCallback(async () => {
    if (!user) return;
    const { data: row } = await (supabase as any)
      .from("member_records")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();
    if (row) hydrate(row as MemberRecord);
  }, [user, hydrate]);

  // Coming back from Stripe: poll until the webhook marks the payment complete.
  useEffect(() => {
    if (!confirmingPayment) return;
    if (record?.payment_status === "complete") {
      setConfirmingPayment(false);
      const url = new URL(window.location.href);
      url.searchParams.delete("paid");
      window.history.replaceState({}, "", url.pathname + url.search);
      return;
    }
    const timer = window.setInterval(() => {
      void refresh();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [confirmingPayment, record?.payment_status, refresh]);

  const startCheckout = useCallback(async () => {
    setStartingCheckout(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {},
      });
      let message = error?.message ?? null;
      if (error && (error as any).context?.json) {
        try {
          const body = await (error as any).context.json();
          if (body?.error) message = body.error;
        } catch (_e) {
          // Keep the original message.
        }
      }
      const url = (data as { url?: string; error?: string } | null)?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      toast.error(
        (data as { error?: string } | null)?.error ??
          message ??
          "We could not open the payment page.",
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "We could not open the payment page.");
    } finally {
      setStartingCheckout(false);
    }
  }, []);


  const patch = useCallback(
    async (values: Record<string, unknown>, next: StepKey, stage?: string) => {
      if (!record) return;
      setSaving(true);
      const { error } = await (supabase as any)
        .from("member_records")
        .update(values)
        .eq("id", record.id);
      setSaving(false);
      if (error) {
        toast.error("We could not save that. Please try again.");
        return;
      }
      setRecord({ ...record, ...(values as Partial<MemberRecord>) });
      setStepKey(next);
      await saveState(record.id, next);
      if (stage) void pushStage(stage);
    },
    [record, saveState, pushStage],
  );

  const goTo = useCallback(
    async (next: StepKey) => {
      setStepKey(next);
      if (record) await saveState(record.id, next);
    },
    [record, saveState],
  );

  // ---- Which steps this member sees, in order ----
  const stepOrder = useMemo<StepKey[]>(() => {
    const steps: StepKey[] = [
      "principal_officer",
      "legal_name",
      "phone",
      "legal_address",
      "shipping_address",
      "home_situation",
    ];
    if (homeSituation === "us" || homeSituation === "us_kids" || homeSituation === "us_kids_grown")
      steps.push("spouse");
    if (
      homeSituation === "us_kids" ||
      homeSituation === "me_kids" ||
      homeSituation === "us_kids_grown" ||
      homeSituation === "me_kids_grown"
    )
      steps.push("adult_children");

    steps.push("finances", "una_name", "address");
    if (record?.ca_address_option === "platform") steps.push("lease", "payment");
    steps.push("complete");
    return steps;
  }, [homeSituation, record?.ca_address_option]);

  const currentIndex = Math.max(0, stepOrder.indexOf(stepKey));
  const progress = Math.round(((currentIndex + 1) / stepOrder.length) * 100);

  const back = useCallback(() => {
    const prev = stepOrder[Math.max(0, currentIndex - 1)];
    void goTo(prev);
  }, [stepOrder, currentIndex, goTo]);

  const nextAfter = useCallback(
    (key: StepKey): StepKey => {
      const idx = stepOrder.indexOf(key);
      return stepOrder[Math.min(stepOrder.length - 1, idx + 1)];
    },
    [stepOrder],
  );

  const warnings = useMemo(() => nameWarnings(unaTitleCase(unaName)), [unaName]);

  const meaSigned = record?.mea_status === "signed";

  // The agreement panel polls the record so the webhook's signature shows up here.
  const agreementBlock = record
    ? meaSigned
      ? <AgreementSignedCard />
      : <AgreementPanel member={record} onPoll={refresh} />
    : null;

  // ---- Name submission ----
  const submitName = async () => {
    if (!record || !unaName.trim()) return;
    const cleanName = unaTitleCase(unaName);
    setUnaName(cleanName);
    setSaving(true);
    const { data, error } = await supabase.functions.invoke("submit-una-name", {
      body: { name: cleanName },
    });
    setSaving(false);
    if (error) {
      let message = "We could not send that name. Please try again.";
      if (error.context && typeof error.context.json === "function") {
        const body = await error.context.json().catch(() => null);
        if (body && typeof body.error === "string" && body.error.trim()) message = body.error;
      } else if (error.message) {
        message = error.message;
      }
      toast.error(message);
      return;
    }
    const submittedName = typeof data?.name === "string" ? data.name : cleanName;
    setUnaName(submittedName);
    setRecord({
      ...record,
      una_name: submittedName,
      una_name_status: "submitted",
      una_name_review_note: null,
    });
    setFixingName(false);
    setNameChecked(false);
    toast.success("The name has been submitted for review.");
  };

  const confirmName = async () => {
    if (!record) return;
    setSaving(true);
    const stamp = new Date().toISOString();
    const { error } = await (supabase as any)
      .from("member_records")
      .update({ una_name_confirmed_at: stamp })
      .eq("id", record.id);
    setSaving(false);
    if (error) {
      toast.error(error.message || "We could not confirm that name. Please try again.");
      return;
    }
    setRecord({ ...record, una_name_confirmed_at: stamp });
    setNameCelebration(record.una_name ?? "");
    void pushStage("name_confirmed");
  };

  // ---- Address choices ----
  const saveOwnAddress = async () => {
    if (!record) return;
    if (addressLooksLikeMailbox(caAddress)) {
      toast.error("The state needs a real street address here, not a mailbox or a box number.");
      return;
    }
    setSaving(true);
    const { data, error } = await supabase.functions.invoke("save-ca-address", {
      body: { ca_address: caAddress },
    });
    setSaving(false);
    if (error || !(data as { ok?: boolean } | null)?.ok) {
      toast.error("We could not save that. Please try again.");
      return;
    }
    await refresh();
    setStepKey("complete");
    await saveState(record.id, "complete", { completed_at: new Date().toISOString() });
  };

  const pauseForOwnSolution = async () => {
    if (!record) return;
    await (supabase as any).from("member_records").update({ ca_address_option: "self_sourced" }).eq("id", record.id);
    setRecord({ ...record, ca_address_option: "self_sourced" });
    await saveState(record.id, "address", { paused_at_step: "address" });
    setAddressChoice("self_sourced");
    void pushStage("address_pending");
  };

  const reserveSuite = async () => {
    if (!record) return;
    setSaving(true);
    const { data, error } = await supabase.functions.invoke("reserve-suite", { body: {} });
    setSaving(false);
    if (error || !data?.suite_number) {
      toast.error("We could not reserve your suite. Please try again.");
      return;
    }
    await refresh();
    await goTo("lease");
    toast.success(`Suite ${data.suite_number} is yours.`);
  };

  // The lease lane opens itself. We only record that it was sent, server side.
  useEffect(() => {
    if (stepKey !== "lease") return;
    if (leaseSentRef.current) return;
    if (!record?.suite_number) return;
    if (record.lease_status !== "not_started") return;
    leaseSentRef.current = true;
    void pushStage("lease_sent");
  }, [stepKey, record, pushStage]);

  // The holding step polls the record so an approval flips into the confirmation ceremony automatically.
  useEffect(() => {
    if (record?.una_name_status !== "submitted" || !meaSigned) return;
    const id = window.setInterval(refresh, 5000);
    return () => window.clearInterval(id);
  }, [record?.una_name_status, meaSigned, refresh]);

  // Server truth for a resubmission: at least one earlier decided review row on this record.
  useEffect(() => {
    if (!record?.id || record.una_name_status !== "submitted") return;
    let active = true;
    void (async () => {
      const { count } = await (supabase as any)
        .from("name_review_events")
        .select("id", { count: "exact", head: true })
        .eq("member_record_id", record.id)
        .not("decision", "is", null);
      if (active) setIsResubmission((count ?? 0) > 0);
    })();
    return () => {
      active = false;
    };
  }, [record?.id, record?.una_name_status]);


  if (loading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!record) {
    return (
      <div className="min-h-[100dvh] bg-muted/20 p-4 pt-10">
        <div className="mx-auto w-full max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle>We are getting your file ready</CardTitle>
              <CardDescription>
                Your setup is not open yet. Give it a moment and refresh, and if it stays this way
                reach out to the team and we will sort it quickly.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  const platformAddress = record.suite_number
    ? PLATFORM_ADDRESS_TEMPLATE.replace("{N}", String(record.suite_number))
    : "";

  // Saves one inline edit from the summary and collapses the row.
  const saveInline = async (values: Record<string, unknown>) => {
    if (!record) return;
    setSaving(true);
    const { error } = await (supabase as any)
      .from("member_records")
      .update(values)
      .eq("id", record.id);
    setSaving(false);
    if (error) {
      toast.error("We could not save that. Please try again.");
      return;
    }
    setRecord({ ...record, ...(values as Partial<MemberRecord>) });
    setEditingRow(null);
  };

  const cancelInline = () => {
    if (record) hydrate(record);
    setEditingRow(null);
  };

  const InlineActions = ({ onSave, disabled }: { onSave: () => void; disabled?: boolean }) => (
    <div className="flex gap-2 pt-2">
      <Button type="button" size="sm" onClick={onSave} disabled={disabled || saving} className="min-h-9">
        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        Save
      </Button>
      <Button type="button" size="sm" variant="ghost" onClick={cancelInline} className="min-h-9">
        Cancel
      </Button>
    </div>
  );

  // The five shipping columns, written together so they never drift apart.
  const shippingPatch = (address: AddressValue | null) => ({
    shipping_line1: address ? address.line1.trim() : null,
    shipping_line2: address ? address.line2.trim() || null : null,
    shipping_city: address ? address.city.trim() : null,
    shipping_state: address ? address.state.trim() : null,
    shipping_zip: address ? address.zip.trim() : null,
  });

  // A quiet read-only summary of everything the member has given us. Rows are editable only before confirmation.
  const renderRecordSummary = ({ editable = false }: { editable?: boolean } = {}) => {
    if (!record) return null;

    const legalLines = addressLines(toAddress(record.legal_address));

    const homeLabel = record.home_situation ? (HOME_LABELS[record.home_situation] ?? null) : null;
    const officerIsOther = record.principal_officer_is_self === false;

    const rows: Array<{ key: string; label: string; value: ReactNode; editor: ReactNode }> = [];

    rows.push({
      key: "officer_name",
      label: "First officer's full legal name",
      value: (officerIsOther ? record.principal_officer_name : record.legal_full_name) || (
        <span className="text-muted-foreground">Not given</span>
      ),
      editor: (
        <div className="space-y-2">
          <Input
            aria-label="First officer's full legal name"
            value={officerIsOther ? officerName : legalName}
            onChange={(e) => (officerIsOther ? setOfficerName(e.target.value) : setLegalName(e.target.value))}
            onBlur={() =>
              officerIsOther ? setOfficerName(properName(officerName)) : setLegalName(properName(legalName))
            }
            className="min-h-11"
          />
          <InlineActions
            disabled={!(officerIsOther ? officerName.trim() : legalName.trim())}
            onSave={() =>
              void saveInline(
                officerIsOther
                  ? { principal_officer_name: properName(officerName) }
                  : { legal_full_name: properName(legalName) },
              )
            }
          />
        </div>
      ),
    });

    if (record.spouse_name) {
      rows.push({
        key: "spouse",
        label: "Significant other",
        value: record.spouse_name,
        editor: (
          <div className="space-y-2">
            <Input
              aria-label="Significant other's full name"
              value={spouseName}
              onChange={(e) => setSpouseName(e.target.value)}
              onBlur={() => setSpouseName(properName(spouseName))}
              className="min-h-11"
            />
            <InlineActions
              disabled={!spouseName.trim()}
              onSave={() => void saveInline({ spouse_name: properName(spouseName) || null })}
            />
          </div>
        ),
      });
    }

    if (homeLabel) {
      rows.push({
        key: "home",
        label: "Home life",
        value: homeLabel,
        editor: (
          <div className="space-y-3">
            {HOME_OPTIONS.map((o) => (
              <OptionButton
                key={o.value}
                selected={homeSituation === o.value}
                label={o.label}
                onClick={() => setHomeSituation(o.value)}
              />
            ))}
            <InlineActions
              disabled={!homeSituation}
              onSave={() => void saveInline({ home_situation: homeSituation })}
            />
          </div>
        ),
      });
    }

    if (record.adult_children_count !== null && record.adult_children_count !== undefined) {
      rows.push({
        key: "adult_children",
        label: "Adult children",
        value: String(record.adult_children_count),
        editor: (
          <div className="space-y-2">
            <Input
              aria-label="How many adult children"
              type="number"
              min={0}
              value={adultKidsCount}
              onChange={(e) => setAdultKidsCount(e.target.value)}
              className="min-h-11"
            />
            <InlineActions
              onSave={() => void saveInline({ adult_children_count: Number(adultKidsCount || 0) })}
            />
          </div>
        ),
      });
    }

    rows.push({
      key: "legal",
      label: "Legal address",
      value: legalLines.length ? legalLines.map((line) => <div key={line}>{line}</div>) : (
        <span className="text-muted-foreground">Not given</span>
      ),
      editor: (
        <div className="space-y-3">
          <AddressFields value={legalAddress} onChange={setLegalAddress} idPrefix="summary-legal" />
          <InlineActions
            disabled={!addressIsComplete(legalAddress)}
            onSave={() => void saveInline({ legal_address: legalAddress })}
          />
        </div>
      ),
    });

    const shippingLines = addressLines(shippingAddress);

    rows.push({
      key: "shipping",
      label: shippingChoice === "different" ? "Shipping address" : "Shipping",
      value:
        shippingChoice === "different"
          ? shippingLines.length
            ? shippingLines.map((line) => <div key={line}>{line}</div>)
            : (<span className="text-muted-foreground">Not given</span>)
          : "Ship to my legal address.",
      editor: (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <OptionButton
              selected={shippingChoice === "legal"}
              label="Ship to my legal address."
              onClick={() => setShippingChoice("legal")}
            />
            <OptionButton
              selected={shippingChoice === "different"}
              label="Ship to a different address."
              onClick={() => setShippingChoice("different")}
            />
          </div>
          {shippingChoice === "different" && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                We cannot ship to a PO Box. We use FedEx 2-Day Air to send your documents safely.
              </p>
              <AddressFields value={shippingAddress} onChange={setShippingAddress} idPrefix="summary-ship" />
            </div>
          )}
          <InlineActions
            disabled={
              shippingChoice === null ||
              (shippingChoice === "different" && !addressIsComplete(shippingAddress))
            }
            onSave={() => {
              const destination = shippingChoice === "different" ? shippingAddress : legalAddress;
              if (addressLooksLikeMailbox(destination)) {
                toast.error("We cannot ship to a PO Box or mailbox service. Please use a real street address.");
                return;
              }
              void saveInline(shippingPatch(shippingChoice === "different" ? shippingAddress : null));
            }}
          />
        </div>
      ),
    });


    return (
      <div className="space-y-4">
        {rows.map((r) => (
          <div key={r.key}>
            <div className="flex flex-nowrap items-baseline justify-between gap-2">
              <div className="min-w-0 text-xs text-muted-foreground">{r.label}</div>
              {editable && editingRow !== r.key ? (
                <button
                  type="button"
                  onClick={() => {
                    if (record) hydrate(record);
                    setEditingRow(r.key);
                  }}
                  className="shrink-0 whitespace-nowrap text-xs text-muted-foreground underline underline-offset-4"
                >
                  Edit
                </button>
              ) : null}
            </div>
            {editable && editingRow === r.key ? (
              <div className="mt-1">{r.editor}</div>
            ) : (
              <div className="mt-1 text-sm leading-relaxed">{r.value}</div>
            )}
          </div>
        ))}
      </div>
    );
  };


  const renderNameConfirmStep = () => {
    if (!record) return null;

        if (nameCelebration) {
          return (
            <StepShell
              title="Written in stone"
              onContinue={() => {
                setNameCelebration(null);
                void goTo("address");
              }}
              continueLabel="Next step"
            >
              <div className="py-6 text-center">
                <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
                <p className="mt-4 text-xl font-semibold tracking-wide">
                  {nameCelebration.toUpperCase()} stands confirmed.
                </p>
              </div>
            </StepShell>
          );
        }

        return (
          <StepShell title="One last look">
            <p className="py-4 text-center text-2xl font-semibold leading-snug sm:text-3xl">
              {record.una_name}
            </p>
            <div className="space-y-1 text-center">
              <button
                type="button"
                onClick={() => {
                  setNameChecked(false);
                  setFixingName(true);
                  void goTo("una_name");
                }}
                className="text-sm text-muted-foreground underline underline-offset-4"
              >
                Need a different name? Send a new one for review.
              </button>
              <p className="text-xs text-muted-foreground">
                A new name goes back through our manual review, so you would wait on approval again.
              </p>
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Your Association's name was approved. Now let's make sure everything looks correct
              before we lock it in and get started with the formation process. If it looks good,
              confirm by checking the box and hit the button. If not, update your info right here.
            </p>
            {renderRecordSummary({ editable: true })}
            <label className="flex items-start gap-3 rounded-lg border p-4 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 h-5 w-5"
                checked={nameChecked}
                onChange={(e) => setNameChecked(e.target.checked)}
              />
              <span>I have checked every detail above and it is all correct.</span>
            </label>
            <Button
              type="button"
              onClick={confirmName}
              disabled={!nameChecked || saving || editingRow !== null}
              className="min-h-11 w-full"
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirm. This is all correct.
            </Button>
          </StepShell>
        );

      
  };

  const renderAddressStep = () => {
    if (!record) return null;


        if (record.una_name_status === "approved" && !record.una_name_confirmed_at) {
          return renderNameConfirmStep();
        }

        if (record.una_name_status !== "approved" || !meaSigned) {
          const waitingOnName = record.una_name_status !== "approved";
          return (
            <StepShell title="Almost there" onBack={record.una_name_confirmed_at ? undefined : back}>
              <Alert>
                <Clock className="h-4 w-4" />
                <AlertDescription>
                  {waitingOnName && !meaSigned
                    ? "This step opens once your association name is approved and your Member Engagement Agreement is signed."
                    : waitingOnName
                      ? "Your agreement is signed. This step opens as soon as your association name is approved and confirmed. We will email you."
                      : "Your name is approved. This step opens once your Member Engagement Agreement is signed."}
                </AlertDescription>
              </Alert>
              {!meaSigned ? agreementBlock : null}
            </StepShell>
          );
        }

        if (addressChoice === "self_sourced" && !addressIsComplete(caAddress)) {
          return (
            <StepShell title="Your spot is saved" onBack={record.una_name_confirmed_at ? undefined : back}>
              <Alert>
                <ShieldCheck className="h-4 w-4" />
                <AlertDescription>
                  Come back to this exact step any time. Nothing is lost.
                </AlertDescription>
              </Alert>
              <p className="text-sm text-muted-foreground">
                When you have a California street address, enter it below and we will keep going.
              </p>
              <AddressFields value={caAddress} onChange={setCaAddress} idPrefix="ca" />
              <Button
                type="button"
                onClick={saveOwnAddress}
                disabled={!addressIsComplete(caAddress) || saving}
                className="min-h-11 w-full sm:w-auto"
              >
                Save my address
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setAddressChoice(null)}
                className="min-h-11"
              >
                Pick a different option
              </Button>
            </StepShell>
          );
        }

        if (addressChoice === "own") {
          return (
            <StepShell
              title="Your California address"
              helper="The state needs a real street address here. Mailboxes and box numbers are turned away."
              onBack={() => setAddressChoice(null)}
              onContinue={saveOwnAddress}
              continueLabel="Save my address"
              continueDisabled={!addressIsComplete(caAddress)}
              saving={saving}
            >
              <AddressFields value={caAddress} onChange={setCaAddress} idPrefix="ca-own" />
            </StepShell>
          );
        }

        if (addressChoice === "platform" && !platformConfirmed && !record.suite_number) {
          return (
            <StepShell
              title="Before we reserve your suite number"
              onBack={() => setAddressChoice(null)}
              onContinue={reserveSuite}
              continueLabel="Confirm and reserve my suite"
              saving={saving}
            >
              <div className="space-y-3 rounded-lg bg-muted/40 p-4 text-sm leading-relaxed">
                <p className="font-semibold">This address is NOT to be used as a mailing address.</p>
                <p>We use this address as the principal address when we acquire your EIN and register with the state of California.</p>
                <p>It is not to be used for any other purpose.</p>
                <p>
                  There is a short lease to sign and a{" "}
                  <span className="font-semibold">$19 per month</span> fee for this address.
                </p>
                <p>
                  <span className="font-semibold">What's next?</span> When you confirm here, your
                  suite number is reserved for 14 days. Next you sign the short lease, then confirm
                  the monthly fee, and your Association's address is settled. If you would rather use
                  a different option, hit the back button.
                </p>
              </div>
            </StepShell>
          );
        }

        return (
          <StepShell title="Let's decide on an address for the Association" onBack={record.una_name_confirmed_at ? undefined : back}>
            <p className="text-sm text-muted-foreground">
              The UNA needs an official California street address. It can be one of yours, a new address you acquire, or we can make one for you for a small monthly fee.
            </p>
            <div className="space-y-3">
              <OptionButton
                selected={addressChoice === "own"}
                label="Enter a California street address I already have access to."
                onClick={() => setAddressChoice("own")}
              />
              <OptionButton
                selected={addressChoice === "self_sourced"}
                label="Acquire my own California address and come back."
                onClick={() => void pauseForOwnSolution()}
              />
              <OptionButton
                selected={addressChoice === "platform"}
                label="Use a suite number underneath Wealthy Steward's address."
                onClick={() => setAddressChoice("platform")}
              />
            </div>
          </StepShell>
        );

  };

  const renderStep = () => {
    switch (stepKey) {
      case "principal_officer":
        return (
          <StepShell
            title="Will you serve as the first officer of the Association?"
            helper="For most people the answer is yes. In some cases, you may want a spouse or trusted person. If you don't have someone else in mind already, then choose yes as your answer."
            onContinue={() =>
              patch(
                {
                  principal_officer_is_self: officerIsSelf,
                  principal_officer_name: officerIsSelf ? null : officerName.trim() || null,
                  principal_officer_email: officerIsSelf ? null : officerEmail.trim() || null,
                },
                nextAfter("principal_officer"),
              )
            }
            continueDisabled={
              officerIsSelf === null ||
              (officerIsSelf === false && (!officerName.trim() || !officerEmail.trim()))
            }
            saving={saving}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <OptionButton selected={officerIsSelf === true} label="Yes, that is me." onClick={() => setOfficerIsSelf(true)} />
              <OptionButton
                selected={officerIsSelf === false}
                label="No, someone else will."
                onClick={() => setOfficerIsSelf(false)}
              />
            </div>
            {officerIsSelf === false && (
              <div className="space-y-4 rounded-lg border p-4">
                <div className="space-y-2">
                  <Label htmlFor="officer-name">Their full legal name</Label>
                  <Input
                    id="officer-name"
                    value={officerName}
                    onChange={(e) => setOfficerName(e.target.value)}
                    onBlur={() => setOfficerName(properName(officerName))}
                    className="min-h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="officer-email">Their email</Label>
                  <Input id="officer-email" type="email" value={officerEmail} onChange={(e) => setOfficerEmail(e.target.value)} className="min-h-11" />
                </div>
              </div>
            )}
          </StepShell>
        );

      case "legal_name":
        return (
          <StepShell
            title="Your full legal name"
            helper="Type it exactly as it appears on your driver's license. This is the name that goes on the entity papers, so the spelling matters."
            onBack={back}
            onContinue={() => patch({ legal_full_name: properName(legalName) }, nextAfter("legal_name"), "intake_contact")}
            continueDisabled={!legalName.trim()}
            saving={saving}
          >
            <div className="space-y-2">
              <Label htmlFor="legal-name">Full legal name</Label>
              <Input
                id="legal-name"
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                onBlur={() => setLegalName(properName(legalName))}
                className="min-h-11"
              />
            </div>
          </StepShell>
        );

      case "phone":
        return (
          <StepShell
            title="Your phone number"
            helper="We use this only when something needs a quick word."
            onBack={back}
            onContinue={() => patch({ phone: phone.trim() }, nextAfter("phone"))}
            continueDisabled={phone.trim().length < 7}
            saving={saving}
          >
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="min-h-11" />
            </div>
          </StepShell>
        );

      case "legal_address":
        return (
          <StepShell
            title="What is your legal address?"
            helper="Enter it exactly as it appears on your driver's license."
            onBack={back}
            onContinue={() => patch({ legal_address: legalAddress }, nextAfter("legal_address"))}
            continueDisabled={!addressIsComplete(legalAddress)}
            saving={saving}
          >
            <AddressFields value={legalAddress} onChange={setLegalAddress} idPrefix="legal" />
          </StepShell>
        );

      case "shipping_address":
        return (
          <StepShell
            title="Where should we ship the documents when they are ready?"
            onBack={back}
            onContinue={() => {
              const destination = shippingChoice === "different" ? shippingAddress : legalAddress;
              if (addressLooksLikeMailbox(destination)) {
                toast.error("We cannot ship to a PO Box or mailbox service. Please use a real street address.");
                return;
              }
              patch(
                shippingPatch(shippingChoice === "different" ? shippingAddress : null),
                nextAfter("shipping_address"),
              );
            }}
            continueDisabled={
              shippingChoice === null || (shippingChoice === "different" && !addressIsComplete(shippingAddress))
            }
            saving={saving}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <OptionButton
                selected={shippingChoice === "legal"}
                label="Ship to my legal address."
                onClick={() => setShippingChoice("legal")}
              />
              <OptionButton
                selected={shippingChoice === "different"}
                label="Ship to a different address."
                onClick={() => setShippingChoice("different")}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              We cannot ship to a PO Box. We use FedEx 2-Day Air to send your documents safely and dependably.
            </p>
            {shippingChoice === "different" && (
              <AddressFields value={shippingAddress} onChange={setShippingAddress} idPrefix="shipping" />
            )}
          </StepShell>
        );


      case "home_situation":
        return (
          <StepShell
            title="What is home life like for you?"
            onBack={back}
            onContinue={() =>
              patch({ home_situation: homeSituation }, nextAfter("home_situation"), "intake_household")
            }
            continueDisabled={!homeSituation}
            saving={saving}
          >
            <div className="space-y-3">
              {HOME_OPTIONS.map((o) => (
                <OptionButton
                  key={o.value}
                  selected={homeSituation === o.value}
                  label={o.label}
                  onClick={() => setHomeSituation(o.value)}
                />
              ))}
            </div>
          </StepShell>
        );

      case "spouse":
        return (
          <StepShell
            title="What is your significant other's full name?"
            onBack={back}
            onContinue={() => patch({ spouse_name: properName(spouseName) || null }, nextAfter("spouse"))}
            continueDisabled={!spouseName.trim()}
            saving={saving}
          >
            <Input
              id="spouse"
              aria-label="Significant other's full name"
              value={spouseName}
              onChange={(e) => setSpouseName(e.target.value)}
              onBlur={() => setSpouseName(properName(spouseName))}
              className="min-h-11"
            />
          </StepShell>
        );

      case "adult_children":
        return (
          <StepShell
            title="Are any of your kids 18 or older?"
            onBack={back}
            onContinue={() =>
              patch(
                { adult_children_count: hasAdultKids ? Number(adultKidsCount || 0) : 0 },
                nextAfter("adult_children"),
              )
            }
            continueDisabled={
              hasAdultKids === null || (hasAdultKids && !Number(adultKidsCount || 0))
            }
            saving={saving}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <OptionButton selected={hasAdultKids === true} label="Yes" onClick={() => setHasAdultKids(true)} />
              <OptionButton selected={hasAdultKids === false} label="No" onClick={() => setHasAdultKids(false)} />
            </div>
            {hasAdultKids && (
              <div className="space-y-2">
                <Label htmlFor="adult-count">How many?</Label>
                <Input
                  id="adult-count"
                  type="number"
                  min={1}
                  value={adultKidsCount}
                  onChange={(e) => setAdultKidsCount(e.target.value)}
                  className="min-h-11 max-w-32"
                />
              </div>
            )}
          </StepShell>
        );

      case "finances": {
        const showInterest = financeSegment === "seg_counseled" || financeSegment === "seg_house";
        return (
          <StepShell
            title="Which sounds most like how your finances are handled today?"
            onBack={back}
            onContinue={() =>
              patch(
                {
                  finance_segment: financeSegment,
                  chancery_interest: showInterest ? chanceryInterest : null,
                  chancery_interest_at:
                    showInterest && chanceryInterest !== null ? new Date().toISOString() : null,
                },
                nextAfter("finances"),
                "intake_finances",
              )
            }
            continueDisabled={!financeSegment}
            saving={saving}
          >
            <div className="space-y-3">
              {FINANCE_OPTIONS.map((o) => (
                <OptionButton
                  key={o.value}
                  selected={financeSegment === o.value}
                  label={o.label}
                  onClick={() => setFinanceSegment(o.value)}
                />
              ))}
            </div>
            {showInterest && (
              <div className="space-y-3 border-t pt-4">
                <p className="text-sm">
                  Some families want their stewardship administration handled for them, start to
                  finish. Would you be open to a conversation about that?
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <OptionButton
                    selected={chanceryInterest === true}
                    label="Yes, I'd be open to that"
                    onClick={() => setChanceryInterest(true)}
                  />
                  <OptionButton
                    selected={chanceryInterest === false}
                    label="Not right now"
                    onClick={() => setChanceryInterest(false)}
                  />
                </div>
              </div>
            )}
          </StepShell>
        );
      }


      case "una_name": {
        if (record.una_name_status === "submitted") {
          if (meaSigned) {
            return (
              <StepShell
                title={isResubmission ? "Resubmitted. You're all set." : "You're all set for now"}
                onBack={back}
                backLabel="Review my earlier answers"
              >
                <div className="space-y-4 text-sm leading-relaxed">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                    <span>Your agreement is signed.</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                    <span>
                      <span className="font-medium italic">{record.una_name}</span>{" "}
                      {isResubmission ? "is with our team for a fresh review." : "is with our team for review."}
                    </span>

                  </div>
                  <p>
                    Once your name has been reviewed and approved, this page will automatically show
                    your next step. All you need to do is refresh. We will send you an email to{" "}
                    <span className="font-medium">{record.email}</span> with a link to come right back
                    here when the next step is ready.
                  </p>
                </div>
              </StepShell>
            );
          }

          return (
            <StepShell title="May we have your signed permission to get to work?" onBack={back}>
              <div className="space-y-3 text-sm leading-relaxed">
                <p>Congrats {record.first_name}! You just got through the hardest part of this whole process.</p>
                <p>
                  <span className="font-medium italic">{record.una_name}</span> has been submitted for manual review.
                </p>
                <p>
                  We will follow up via email when your next step is ready. When it is, you can come
                  right back here and it'll be waiting for you with a refresh of the page.
                </p>
                <p>
                  Now, while you wait, let's get the official written permission from you that we need
                  to get to work.
                </p>
              </div>
              {agreementBlock}
            </StepShell>
          );
        }


        if (record.una_name_status === "approved" && !record.una_name_confirmed_at && !fixingName) {
          return renderNameConfirmStep();
        }

        if (record.una_name_status === "approved" && record.una_name_confirmed_at) {
          return renderAddressStep();
        }


        return (
          <StepShell
            title="Name your association"
            onBack={back}
            onContinue={submitName}
            continueLabel="Submit for review"
            continueDisabled={!unaName.trim() || warnings.length > 0}
            saving={saving}
          >
            {record.una_name_status === "change_requested" && record.una_name_review_note && (
              <Alert>
                <Sparkles className="h-4 w-4" />
                <AlertDescription>
                  <span className="font-medium">A note from the team: </span>
                  {record.una_name_review_note}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-3 rounded-lg bg-muted/40 p-4 text-sm leading-relaxed">
              <p>
                Perhaps the most important step. This is not quite like naming a child, but close.
                Just remove the pressure, this should be fun.
              </p>
              <p>How do you name the association? We keep it simple, like always. First, some guidelines.</p>
              <ol className="list-decimal space-y-2 pl-5">
                <li>
                  The name should not come from family names or current businesses. It is not an
                  extension of any business. It should stand clearly and confidently on its own.
                </li>
                <li>It should sound like what it is, an association with religious character.</li>
                <li>Two to three words is the sweet spot, four at most.</li>
                <li>You can use numbers or abbreviations if you truly want to, but they are not recommended.</li>
              </ol>
              <p className="font-medium">Here's how we like to name the Association:</p>
              <p>
                Think of names, symbols, sayings, phrases, scriptures, places, or quotes that are
                personally inspiring, meaningful or memorable to you or your family. Then add a term
                that signifies this is an association of individuals. You can use words like
                Association, Collective, Society, Group, International, Alliance, Affiliation,
                Brotherhood, Sisterhood, Fellowship, Project, Council, Network, Mission, Assembly,
                or any other word you love that suggests clearly an association.
              </p>
              <p>
                Sometimes the name chooses us, sometimes it's less obvious and takes some real
                thought. It's okay either way. Let it be fun.
              </p>
              <p>
                This name is generational and something you will be living beside for the rest of
                your life, so give it real meaningful thought.
              </p>
            </div>

            <div className="rounded-lg border-2 border-amber-400/70 bg-amber-50/70 p-4 text-sm leading-relaxed dark:bg-amber-950/20">
              <span className="font-semibold">NOTE:</span> Do NOT use the words family, trust,
              enterprises, incorporated, corporation, company, foundation, charity, co-op, or
              cooperative. Do not lead with "The" in the name.
            </div>

            <div className="space-y-2">
              <Label htmlFor="una-name">Association name</Label>
              <Input
                id="una-name"
                value={unaName}
                onChange={(e) => {
                  setUnaName(e.target.value);
                }}
                onBlur={() => setUnaName(unaTitleCase(unaName))}
                className="min-h-11"
                aria-invalid={warnings.length > 0}
              />
              {warnings.length > 0 && (
                <div className="space-y-1 pt-1">
                  {warnings.map((w) => (
                    <p key={w} className="text-sm text-destructive">
                      {w}
                    </p>
                  ))}
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                Our team manually checks every submitted name for accuracy before we begin forming the Association.
              </p>
            </div>


            {record.una_name_status === "change_requested" ? agreementBlock : null}
          </StepShell>
        );
      }

      case "address":
        return renderAddressStep();

      case "lease":
        return (
          <StepShell title="Sign your lease" onBack={record.una_name_confirmed_at ? undefined : back}>
            {platformAddress && (
              <div className="rounded-lg border p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Your address</p>
                <p className="mt-1 font-medium">{platformAddress}</p>
              </div>
            )}


            {record.lease_status === "signed" ? (
              <LeaseSignedCard />
            ) : (
              <LeasePanel member={record} onPoll={refresh} />
            )}


            {record.lease_status === "signed" && (
              <Button variant="secondary" onClick={() => goTo("complete")} className="min-h-11">
                Continue
              </Button>
            )}
          </StepShell>
        );

      case "payment": {
        if (record.lease_status !== "signed") {
          return (
            <StepShell title="Your lease first" onBack={() => goTo("lease")}>
              <Alert>
                <Clock className="h-4 w-4" />
                <AlertDescription>
                  This step opens once your signed lease reaches us. It usually takes a moment.
                </AlertDescription>
              </Alert>
              <Button variant="ghost" onClick={refresh} className="min-h-11">
                Just signed? Refresh
              </Button>
            </StepShell>
          );
        }

        if (confirmingPayment && record.payment_status !== "complete") {
          return (
            <StepShell title="We are confirming your payment">
              <div className="flex items-center gap-3 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>This takes just a moment. You can stay right here.</span>
              </div>
              <Button variant="ghost" onClick={refresh} className="min-h-11">
                Just completed your payment? Refresh
              </Button>
            </StepShell>
          );
        }

        return (
          <StepShell title="Set up your monthly fee" onBack={() => goTo("lease")}>
            <p className="text-sm leading-relaxed">
              The suite is {PLATFORM_ADDRESS_MONTHLY_FEE} dollars per month. Set it up once and you
              are done.
            </p>
            <Button
              onClick={startCheckout}
              disabled={startingCheckout}
              className="min-h-11 w-full sm:w-auto"
            >
              {startingCheckout ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Opening payment
                </>
              ) : (
                "Set up the $19 monthly address fee"
              )}
            </Button>
            <p className="text-sm text-muted-foreground">
              Payment opens full screen with Stripe, our payment processor. When you finish, we
              bring you right back here.
            </p>
            <Button variant="ghost" onClick={refresh} className="min-h-11">
              Just completed your payment? Refresh
            </Button>
          </StepShell>
        );
      }

      case "complete":
      default:
        return (
          <CompletionCelebration
            firstName={record.first_name}
            unaName={record.una_name}
            paymentComplete={record.payment_status === "complete"}
            onRefresh={refresh}
            paymentCard={
              isSuitePath(record) && record.payment_status !== "complete" ? (
                <div className="rounded-lg border p-4 space-y-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Your monthly address fee
                  </p>
                  <p className="text-sm leading-relaxed">
                    The suite is {PLATFORM_ADDRESS_MONTHLY_FEE} dollars per month. Set it up once
                    and you are done. We are already working on your Association.
                  </p>
                  <Button
                    onClick={startCheckout}
                    disabled={startingCheckout}
                    className="min-h-11 w-full sm:w-auto"
                  >
                    {startingCheckout ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Opening payment
                      </>
                    ) : (
                      "Set up the $19 monthly address fee"
                    )}
                  </Button>
                </div>
              ) : null
            }
            associationCard={
              record.una_name ? (
                <div className="rounded-lg border p-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Your association
                  </p>
                  <p className="mt-1 font-medium">{record.una_name}</p>
                </div>
              ) : null
            }
            addressCard={
              platformAddress ? (
                <div className="rounded-lg border p-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Your registered address
                  </p>
                  <p className="mt-1 font-medium">{platformAddress}</p>
                </div>
              ) : null
            }
          />
        );
    }
  };

  const intakeSteps = stepOrder.filter(
    (s) => s !== "address" && s !== "lease" && s !== "payment" && s !== "complete",
  );
  const intakeIndex = (intakeSteps as StepKey[]).indexOf(stepKey);
  const nameIsBeingChosen =
    record.una_name_status === "not_submitted" ||
    record.una_name_status === "change_requested" ||
    fixingName;
  const inIntake = intakeIndex >= 0 && !(stepKey === "una_name" && !nameIsBeingChosen);
  const phase = inIntake ? 1 : resolveOnboardingPhase(record);

  return (
    <OnboardingShell
      currentPhase={phase}
      stepCaption={
        inIntake ? `Step ${intakeIndex + 1} of ${intakeSteps.length}` : undefined
      }
    >
      {record.suite_number ? (
        <div className="mb-3 flex justify-end">
          <Badge variant="secondary">Suite {record.suite_number}</Badge>
        </div>
      ) : null}
      {renderStep()}
    </OnboardingShell>
  );
}

